import { Box, Button, CircularProgress, Divider, IconButton, Tab, Tabs } from '@mui/material';
import { useTrackerContext } from '../../../context';
import { useEffect, useRef, useState } from 'react';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import CheckboxFillIcon from 'remixicon-react/CheckboxFillIcon';
import { ConfigEntryType } from 'config/config';
import TaskDetailsForm from './TaskDetailsForm';
import { TaskFormData } from '../../../hooks/useAddTaskForm';
import { useFormContext } from 'react-hook-form';
import TaskCommentsBox from './TaskCommentsBox';
import { useObjectCommentsQuery } from '../../../queries/useTrackerTasksQuery';
import Chat3FillIcon from 'remixicon-react/Chat3FillIcon';
import Chat3LineIcon from 'remixicon-react/Chat3LineIcon';
import CheckboxLineIcon from 'remixicon-react/CheckboxLineIcon';
import { useTrackerDetailsQuery } from '@buildwise/libs/trackers/queries';

type EditTaskProps = {
  taskFields: ConfigEntryType[];
  onSubmit: () => void;
};

function EditTask({ taskFields, onSubmit }: EditTaskProps) {
  const {
    state: { trackerId, selectedTask },
    dispatch,
  } = useTrackerContext();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const [tab, setTab] = useState('task');
  const {
    formState: { isSubmitting },
  } = useFormContext<TaskFormData>();
  const taskContainerRef = useRef<HTMLDivElement | null>(null);
  const { comments } = useObjectCommentsQuery({
    objectId: selectedTask?.id as string,
    objectName: 'Task',
    objectType: 'Comment',
  });

  useEffect(() => {
    if (!taskContainerRef.current) return;

    const commentsBox = taskContainerRef.current.querySelector('.task-comments');
    if (!commentsBox) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (taskContainerRef.current) {
          const isScrollable =
            taskContainerRef.current.scrollHeight > taskContainerRef.current.clientHeight;
          if (isScrollable) {
            setTab(entry.isIntersecting ? 'comments' : 'task');
          }
        }
      },
      { root: taskContainerRef.current, threshold: 0.5 }, // 50% visibility needed
    );

    observer.observe(commentsBox);

    return () => observer.disconnect();
  }, [taskContainerRef.current]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      height="100%"
    >
      <Tabs
        value={tab}
        onChange={(_: unknown, newValue: string) => {
          setTab(newValue);

          // Scroll to comments
          if (newValue === 'comments' && taskContainerRef.current) {
            const commentsEle = taskContainerRef.current.getElementsByClassName(
              'article-comments',
            )?.[0] as HTMLDivElement;
            if (commentsEle) {
              commentsEle.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } else {
            // Scroll to top
            taskContainerRef.current?.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          }
        }}
        sx={{ px: 1, pt: 1 }}
      >
        <Tab
          icon={tab === 'task' ? <CheckboxFillIcon size={18} /> : <CheckboxLineIcon size={18} />}
          iconPosition="start"
          key="task"
          value="task"
          label="Task"
        />
        <Tab
          icon={tab === 'comments' ? <Chat3FillIcon size={18} /> : <Chat3LineIcon size={18} />}
          iconPosition="start"
          key="comments"
          value="comments"
          label={`Comments ${comments.length ? `(${comments.length})` : ''}`}
        />
        <IconButton
          size="small"
          sx={{ position: 'absolute', right: 0 }}
          onClick={() => {
            dispatch({
              payload: {
                selectedTask: undefined,
              },
            });
          }}
        >
          <CloseLineIcon size={26} />
        </IconButton>
      </Tabs>
      {selectedTask && (
        <Box flex={1} overflow="auto" ref={taskContainerRef}>
          <Box p={2}>
            <TaskDetailsForm
              trackerFieldsConfigName={tracker?.configNameForInputFields || ''}
              taskFields={taskFields}
            />
          </Box>
          <Divider sx={{ mx: 3 }} />
          <TaskCommentsBox comments={comments} selectedTask={selectedTask} />
        </Box>
      )}
      <Box>
        <Divider />
        <Box display="flex" justifyContent={'flex-end'} gap={1} p={2}>
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : (
              'Save'
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default EditTask;
